import { useEffect } from "react";
import type { NewsItem } from "@/mocks/news";
import { newsList } from "@/mocks/news";
import { categories } from "@/mocks/news";

type Props = {
  article: NewsItem | null;
  onClose: () => void;
  onSelect: (a: NewsItem) => void;
};

function catLabel(key: string) {
  const found = categories.find((c) => c.key === key);
  return found ? found.label : key;
}

export default function ArticleModal({ article, onClose, onSelect }: Props) {
  useEffect(() => {
    if (!article) return;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [article, onClose]);

  useEffect(() => {
    if (article) window.scrollTo({ top: 0 });
  }, [article]);

  if (!article) return null;

  const related = newsList
    .filter((n) => n.category === article.category && n.id !== article.id)
    .slice(0, 4);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/70 p-0 md:p-6" onClick={onClose}>
      <div
        className="relative flex h-full w-full flex-col overflow-hidden bg-background-50 md:h-[88vh] md:max-w-4xl md:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={article.title}
      >
        <div className="flex items-center justify-between gap-3 border-b border-background-200 bg-background-50 px-4 py-3 md:px-6">
          <span className="flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-article-line text-primary-500"></i>
            <span className="hidden sm:inline">海角爆 · 原文阅读</span>
          </span>
          <button
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-primary-100 transition-colors cursor-pointer"
            aria-label="关闭阅读"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-2xl px-5 py-8 md:px-8">
            <div className="flex items-center gap-2 text-xs">
              <span className="rounded-md bg-primary-100 px-2.5 py-1 font-medium text-primary-700">{catLabel(article.category)}</span>
              <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-secondary-900">{article.tag}</span>
              <span className="text-foreground-500 ml-auto">{article.time}</span>
            </div>
            <h1 className="mt-5 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              {article.title}
            </h1>
            <p className="mt-3 text-sm text-foreground-500 flex items-center gap-3">
              <span><i className="ri-fire-line mr-1 text-primary-500"></i>{article.views} 热度</span>
              <span><i className="ri-flashlight-line mr-1 text-accent-600"></i>实时更新</span>
            </p>
            <div className="mt-5 h-64 md:h-80 overflow-hidden rounded-xl">
              <img src={article.image} alt={article.title} className="h-full w-full object-cover" loading="lazy" />
            </div>
            <div className="prose-cap mt-6 space-y-4 text-[15px] leading-7 text-foreground-800">
              {article.content.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
              <p className="rounded-lg bg-background-100 p-3 text-sm text-foreground-600">
                <i className="ri-information-line mr-1 text-accent-600"></i>
                本文为 {article.time} 更新的快讯内容，更多实时进展请回到首页继续追更。海角爆将持续第一时间同步热门事件新进展。
              </p>
            </div>
          </div>

          <div className="border-t border-background-200 bg-background-100/60 px-5 py-6 md:px-8">
            <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-thumb-up-line text-primary-500"></i> 相关推荐
            </h3>
            {related.length === 0 ? (
              <p className="mt-3 text-sm text-foreground-500">更多同栏目快讯正在路上，随时刷新看看。</p>
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {related.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => onSelect(r)}
                    className="flex items-center gap-3 rounded-xl bg-background-50 p-3 text-left hover:bg-primary-100/50 transition-colors cursor-pointer"
                  >
                    <div className="h-16 w-24 flex-shrink-0 overflow-hidden rounded-lg">
                      <img src={r.image} alt={r.title} className="h-full w-full object-cover" loading="lazy" />
                    </div>
                    <div className="min-w-0">
                      <p className="line-clamp-2 text-sm font-medium text-foreground-900">{r.title}</p>
                      <p className="mt-1 flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-flashlight-line text-accent-600"></i>{r.time}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}